import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Archive, Search, RotateCcw, Trash2, Calendar, MapPin, Users, GraduationCap, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SessionsArchivedPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      // Fetch both archived and completed sessions
      const [archivedRes, completedRes] = await Promise.all([
        fetch(`${API}/admin/sessions?status=archived`, { credentials: 'include' }),
        fetch(`${API}/admin/sessions?status=completed`, { credentials: 'include' })
      ]);
      
      const archived = archivedRes.ok ? await archivedRes.json() : [];
      const completed = completedRes.ok ? await completedRes.json() : [];
      
      // Deduplicate by session_id to avoid React key warnings
      const allSessions = [...archived, ...completed];
      const uniqueSessions = allSessions.filter((session, index, self) => 
        index === self.findIndex(s => s.session_id === session.session_id)
      );
      
      setSessions(uniqueSessions);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleRestore = async (sessionId) => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/restore`, {
        method: 'PUT', credentials: 'include'
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        toast.success('Session restaurée');
      }
    } catch (e) { toast.error('Erreur lors de la restauration'); }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Supprimer définitivement cette session ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        toast.success('Session supprimée');
      }
    } catch (e) { toast.error('Erreur lors de la suppression'); }
  };

  const filtered = sessions.filter(s =>
    s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.course_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="sessions-archived-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Archive className="h-6 w-6 text-slate-500" />
            Sessions archivées
          </h1>
          <p className="text-slate-500 mt-1">Sessions de formation terminées ou archivées</p>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher une session archivée..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-archived"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-sm text-slate-600">Total archivées</p>
            <p className="text-2xl font-bold text-slate-900">{sessions.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-sm text-slate-600">Terminées</p>
            <p className="text-2xl font-bold text-blue-600">{sessions.filter(s => s.status === 'completed').length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-sm text-slate-600">Archivées</p>
            <p className="text-2xl font-bold text-slate-600">{sessions.filter(s => s.status === 'archived').length}</p>
          </CardContent></Card>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Archive className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune session archivée</h3>
                <p className="text-slate-600">Les sessions terminées apparaîtront ici</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Session</TableHead>
                    <TableHead>Formation</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lieu</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(session => (
                    <TableRow key={session.session_id} data-testid={`archived-row-${session.session_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <GraduationCap className="h-5 w-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{session.title}</p>
                            <p className="text-xs text-slate-500">{session.trainer}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{session.course_title}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Calendar className="h-3.5 w-3.5" />
                          {session.date}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="h-3.5 w-3.5" />
                          {session.location}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={session.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                          {session.status === 'completed' ? 'Terminée' : 'Archivée'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleRestore(session.session_id)}>
                              <RotateCcw className="h-4 w-4 mr-2" />Restaurer
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(session.session_id)}>
                              <Trash2 className="h-4 w-4 mr-2" />Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SessionsArchivedPage;
